import { useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import { Loader2, Sparkles, ArrowRight, RotateCcw, Info, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "971589656886";

const buildWhatsAppMessage = (form, result, lang) => {
  const itineraryId = (result && result.id) || "—";
  const interests = (form.interests || []).join(", ") || "Not specified";

  // Truncate itinerary preview to keep URL within safe length
  const preview = (result?.itinerary || "")
    .replace(/[#*_`]/g, "")
    .slice(0, 900)
    .trim();

  const msg = `Hi The China Explorer! 🇨🇳

I just generated an AI itinerary on your website and would love a quote / next steps.

📋 *My Trip Request*
• Duration: ${form.duration} days
• Travelers: ${form.travelers}
• Arrival city: ${form.arrival_city}
• Budget: ${form.budget}
• Interests: ${interests}
• Plan ID: ${itineraryId}
• Language: ${lang}

📝 *Itinerary preview (auto-generated by Mei)*
${preview}${preview.length >= 900 ? "..." : ""}

Please send me the full quote and help me finalize bookings. Thank you!`;

  return msg;
};

const ItineraryBuilder = () => {
  const { t, lang } = useLang();
  const tt = t.itinerary;

  const [form, setForm] = useState({
    duration: 7,
    travelers: 2,
    arrival_city: "Beijing",
    budget: "mid",
    interests: [],
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const toggleInterest = (val) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(val)
        ? f.interests.filter((x) => x !== val)
        : [...f.interests, val],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await axios.post(`${API}/itinerary`, {
        duration: Number(form.duration),
        travelers: Number(form.travelers),
        arrival_city: form.arrival_city,
        budget: form.budget,
        interests: form.interests,
        notes: form.notes,
        language: lang,
      }, { timeout: 120000 });
      setResult(res.data);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || "Failed to generate itinerary.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
  };

  return (
    <section
      id="itinerary"
      data-testid="itinerary-section"
      className="bg-[#EAE6DF] py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <SectionReveal className="max-w-3xl">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#2E5D4B] font-semibold mb-4">
            ✦ {tt.eyebrow}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] leading-[1.05]">
            {tt.title}
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
            {tt.subtitle}
          </p>
        </SectionReveal>

        {!result && (
          <SectionReveal delay={0.15} className="mt-14">
            <div className="flex items-start gap-3 bg-[#0E2A5C]/5 border border-[#0E2A5C]/15 p-5 mb-6 text-sm text-[#0E2A5C]">
              <Info size={16} className="mt-0.5 flex-shrink-0" />
              <p>
                Fill in the form — our AI concierge Mei drafts a complete day-by-day plan (morning / afternoon / evening, local dishes & cultural tips). Generation usually takes <strong>20-60 seconds</strong>; please don't refresh the page.
              </p>
            </div>
            <form
              onSubmit={submit}
              data-testid="itinerary-form"
              className="bg-[#F7F5F0] border border-black/5 p-8 md:p-12 space-y-8"
            >
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <Field label={tt.duration}>
                  <input
                    data-testid="itinerary-duration"
                    type="number"
                    min={2}
                    max={30}
                    value={form.duration}
                    onChange={(e) => setForm({ ...form, duration: e.target.value })}
                    className="ip"
                    required
                  />
                </Field>
                <Field label={tt.travelers}>
                  <input
                    data-testid="itinerary-travelers"
                    type="number"
                    min={1}
                    max={20}
                    value={form.travelers}
                    onChange={(e) => setForm({ ...form, travelers: e.target.value })}
                    className="ip"
                    required
                  />
                </Field>
                <Field label={tt.arrival}>
                  <input
                    data-testid="itinerary-arrival"
                    type="text"
                    value={form.arrival_city}
                    onChange={(e) => setForm({ ...form, arrival_city: e.target.value })}
                    className="ip"
                    required
                  />
                </Field>
                <Field label={tt.budget}>
                  <select
                    data-testid="itinerary-budget"
                    value={form.budget}
                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                    className="ip"
                  >
                    <option value="budget">{tt.budgets.budget}</option>
                    <option value="mid">{tt.budgets.mid}</option>
                    <option value="luxury">{tt.budgets.luxury}</option>
                  </select>
                </Field>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mb-3">
                  {tt.interests}
                </p>
                <div className="flex flex-wrap gap-2">
                  {tt.interestOptions.map((opt) => {
                    const active = form.interests.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        data-testid={`interest-${opt}`}
                        onClick={() => toggleInterest(opt)}
                        className={`px-4 py-2 text-sm border transition-all duration-300 ${
                          active
                            ? "bg-[#C62828] text-white border-[#C62828]"
                            : "bg-white text-[#121212] border-black/15 hover:border-[#C62828]"
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field label={tt.notes}>
                <textarea
                  data-testid="itinerary-notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="ip resize-none"
                  placeholder="..."
                />
              </Field>

              {error && (
                <p data-testid="itinerary-error" className="text-sm text-[#C62828]">
                  {error}
                </p>
              )}

              <button
                data-testid="itinerary-submit-button"
                type="submit"
                disabled={loading}
                className="group bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 uppercase tracking-wider text-sm font-semibold inline-flex items-center gap-3 transition-all duration-300"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {tt.generating}
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    {tt.submit}
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </SectionReveal>
        )}

        {result && (
          <SectionReveal className="mt-14">
            <div
              data-testid="itinerary-result"
              className="bg-[#F7F5F0] border border-black/5 p-8 md:p-12"
            >
              <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <h3 className="font-heading text-2xl md:text-3xl text-[#121212]">
                  {tt.result}
                </h3>
                <div className="flex flex-wrap gap-3">
                  <a
                    data-testid="itinerary-whatsapp-button"
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                      buildWhatsAppMessage(form, result, lang)
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#25D366] hover:bg-[#1da851] text-white text-sm uppercase tracking-wider transition-colors font-semibold"
                  >
                    <MessageCircle size={14} /> Send to WhatsApp
                  </a>
                  <button
                    data-testid="itinerary-reset"
                    onClick={reset}
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-black/20 hover:border-[#C62828] text-sm uppercase tracking-wider transition-colors"
                  >
                    <RotateCcw size={14} /> {tt.reset}
                  </button>
                </div>
              </div>
              <div
                data-testid="itinerary-result-markdown"
                className="prose-itinerary text-[#121212] leading-relaxed"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.itinerary || ""}</ReactMarkdown>
              </div>
            </div>
          </SectionReveal>
        )}
      </div>

      <style>{`
        .ip {
          width: 100%;
          background: white;
          border: 1px solid rgba(0,0,0,0.15);
          padding: 0.875rem 1rem;
          color: #121212;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 200ms;
        }
        .ip:focus {
          border-color: #C62828;
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mb-2">
      {label}
    </span>
    {children}
  </label>
);

export default ItineraryBuilder;
